import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateField } from "../../../reduxSlice/profileSlice";

export const validateStep3 = (data) => {
    const errors = {};
    const alphabetRegex = /^[A-Za-z\s]+$/;
    // FIXED: Updated regex to accept alphabets, spaces, and specifically (. , -)
    const nativePlaceRegex = /^[A-Za-z\s.,-]+$/;

    // 1. Petname Validation (Optional with 50-character limit)
    if (data.petName?.trim()) {
        if (!alphabetRegex.test(data.petName)) {
            errors.petName = "Petname must contain alphabets only";
        } else if (data.petName.length > 50) {
            errors.petName = "Petname cannot exceed 50 characters";
        } else if (data.petName.trim().length < 3) {
            errors.petName = "Petname must be at least 3 characters";
        }
    }

    // 2. Maternal Surname Validation (Required with 50-character limit)
    if (!data.maternalSurname?.trim()) {
        errors.maternalSurname = "Maternal Surname is required";
    } else if (!alphabetRegex.test(data.maternalSurname)) {
        errors.maternalSurname = "Maternal Surname must contain alphabets only";
    } else if (data.maternalSurname.length > 50) {
        errors.maternalSurname = "Maternal Surname cannot exceed 50 characters";
    } else if (data.maternalSurname.trim().length < 3) {
        errors.maternalSurname = "Maternal Surname must be at least 3 characters";
    }

    // 3. Native Place Validation (Required with 50-character limit)
    if (!data.nativePlace?.trim()) {
        errors.nativePlace = "Native Place is required";
    } else if (!nativePlaceRegex.test(data.nativePlace)) {
        errors.nativePlace = "Native Place can contain alphabets, spaces, and symbols (. , -) only";
    } else if (data.nativePlace.length > 50) {
        errors.nativePlace = "Native Place cannot exceed 50 characters";
    }

    if (!data.gotra?.trim()) errors.gotra = "Gotra identification is required";

    // 4. Sibling count validation checking (Optional - Only validates boundary if entered)
    ["brothersMarried", "brothersUnmarried", "sistersMarried", "sistersUnmarried"].forEach(field => {
        const rawVal = data[field];

        // Skip required validation if the field is left empty
        if (rawVal === "" || rawVal === undefined || rawVal === null || rawVal === "null" || rawVal === "NaN") {
            return;
        }

        const val = Number(rawVal);
        if (isNaN(val) || val < 0) {
            errors[field] = "Invalid input number";
        } else if (val > 10) {
            errors[field] = "Maximum limit allowed is 10";
        }
    });

    return errors;
};

const Step3FamilyBackground = ({ masters = {} }) => {
    const dispatch = useDispatch();
    const formData = useSelector((state) => state.profile);
    const errors = useSelector((state) => state.profile.errors) || {};

    const gotraOptions = masters.Gotra || [];

    // FIXED: Appended a tooltip validation property descriptor configuration block safely inside your mappings list
    const mainFields = [
        { key: "petName", label: "Petname", isRequired: false, maxLength: 50 },
    { key: "maternalSurname", label: "Maternal Surname (Mamkul)", isRequired: true, maxLength: 50 },
    {
        key: "nativePlace",
            label: "Native Place",
        isRequired: true,
        maxLength: 50,
        tooltip: "Example: Mumbai, Maharashtra"
    }
];

    // FIXED LIVE VALIDATOR INTERCEPTOR: Applies text formatting rules and updates validation arrays on change
    const handleInputChange = (field, value) => {
        let finalValue = value;

        if (field === "petName" || field === "maternalSurname") {
            // Force first letter capitalization instantly
            if (finalValue.length > 0) {
                finalValue = finalValue.charAt(0).toUpperCase() + finalValue.slice(1);
            }
        }

        if (field === "nativePlace") {
            // Strip any numeric keys or non-allowed symbols out completely
            finalValue = finalValue.replace(/[^A-Za-z\s.,-]/g, "");
            // Force first letter capitalization natively
            if (finalValue.length > 0) {
                finalValue = finalValue.charAt(0).toUpperCase() + finalValue.slice(1);
            }
        }

        // 1. Commit the filtered text directly to your profile slice store layer
        dispatch(updateField({ field, value: finalValue }));

        // 2. Perform on-the-fly validation evaluations against the live entry metrics
        const simulatedData = { ...formData, [field]: finalValue };
        const generatedErrors = validateStep3(simulatedData);

        // 3. Re-sync active runtime errors into Redux state alerts array
        dispatch(updateField({
        field: "errors",
        value: {
        ...errors,
            [field]: generatedErrors[field] || ""
        }
    }));
    };

    // Helper function to guarantee inputs always render a valid string number digit instead of "NaN"
    const getSafeInputValue = (value) => {
        if (value === undefined || value === null || value === "" || value === "null" || value === "NaN" || isNaN(value)) {
            return "0";
        }
        return String(value);
    };

    return (
        <div className="form-animate">
            <div className="row">
                {mainFields.map((item) => (
                    <div className="col-md-6" key={item.key}>
                        <div className="inputcontainer mb-3">
                            <h5 className="input_label d-flex align-items-center gap-1">
                                <span>{item.label}</span>
                                {item.isRequired && <span className="m_feild">*</span>}

                                {/* FIXED: Render structural i information text overlay tooltip securely near text label matching layout templates */}
                                {item.tooltip && (
                                    <span
                                        className="text-muted ms-1"
                                        title={item.tooltip}
                                        style={{ cursor: "pointer", fontSize: "13px" }}
                                    >
                                        <i className="fa fa-info-circle"></i>
                                    </span>
                                )}
                            </h5>
                            <input
                                className={`form-control input_style ${errors[item.key] ? "is-invalid" : ""}`}
                            placeholder={`Enter ${item.label}`}
                            maxLength={item.maxLength}
                            value={formData[item.key] || ""}
                            onChange={(e) => handleInputChange(item.key, e.target.value)}
                            />
                            {errors[item.key] && <div className="text-danger small mt-1">{errors[item.key]}</div>}
                        </div>
                    </div>
                ))}

                {/* Gotra Selector/Input component */}
                <div className="col-md-6">
                    <div className="inputcontainer mb-3">
                        <h5 className="input_label">Gotra<span className="m_feild">*</span></h5>
                        {gotraOptions.length > 0 ? (
                            <select
                            className={`form-select input_style ${errors.gotra ? "is-invalid" : ""}`}
                            value={formData.gotra || ""}
                            onChange={(e) => handleInputChange("gotra", e.target.value)}
                            >
                            <option value="">Select Gotra</option>
                        {gotraOptions.map((opt, i) => (
                            <option key={i} value={opt.name.toLowerCase()}>{opt.name}</option>
                            ))}
                            </select>
                            ) : (
                            <input
                            className={`form-control input_style ${errors.gotra ? "is-invalid" : ""}`}
                        placeholder="Enter Gotra"
                        value={formData.gotra || ""}
                        onChange={(e) => handleInputChange("gotra", e.target.value)}
                        />
                        )}
                        {errors.gotra && <div className="text-danger small mt-1">{errors.gotra}</div>}
                    </div>
                </div>

                {/* Brother and Sister Sibling Input Fields */}
                {[
                    { key: "brothersMarried", label: "No. of Brothers Married" },
                { key: "brothersUnmarried", label: "No. of Brothers Unmarried" },
                { key: "sistersMarried", label: "No. of Sisters Married" },
                { key: "sistersUnmarried", label: "No. of Sisters Unmarried" }
                    ].map((item) => (
                    <div className="col-md-6" key={item.key}>
                <div className="inputcontainer mb-3">
                    <h5 className="input_label">{item.label}</h5>
                    <input
                        type="number"
                        min="0"
                        max="10"
                    className={`form-control input_style ${errors[item.key] ? "is-invalid" : ""}`}
                    placeholder="0"
                    value={getSafeInputValue(formData[item.key])}
                    onChange={(e) => handleInputChange(item.key, e.target.value)}
                    />
                    {errors[item.key] && <div className="text-danger small mt-1">{errors[item.key]}</div>}
                </div>
            </div>
            ))}
        </div>
</div>
);
};

export default Step3FamilyBackground;